import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Tag, ArrowRight, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogHub = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (activeCategory) params.append('category', activeCategory);
      const { data } = await axios.get(`${API}/api/blog/posts?${params}`);
      setPosts(data.data);
      setTotalPages(data.pages);
    } catch (err) { console.error('Failed to fetch posts:', err); }
    finally { setLoading(false); }
  }, [activeCategory, page]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/blog/categories`);
      setCategories(data.data);
    } catch (err) { console.error('Failed to fetch categories:', err); }
  }, []);

  useEffect(() => { fetchPosts(); fetchCategories(); }, [fetchPosts, fetchCategories]);

  const filtered = search
    ? posts.filter(p => p.title.toLowerCase().includes(search.toLowerCase()) || (p.excerpt || '').toLowerCase().includes(search.toLowerCase()))
    : posts;

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-blue-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Link to="/" className="text-orange-400 hover:text-orange-300 text-sm font-medium mb-4 inline-block">&larr; Back to RunTheRays</Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Trade <span className="text-orange-500">Insights</span></h1>
          <p className="text-blue-200 text-lg max-w-xl">Expert guides on sourcing, logistics, quality control, and navigating international trade with China.</p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Search + Categories */}
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-orange-500 transition-colors"
              data-testid="blog-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => { setActiveCategory(''); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${!activeCategory ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="blog-cat-all"
            >All</button>
            {categories.map(c => (
              <button key={c.name} onClick={() => { setActiveCategory(c.name); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === c.name ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`blog-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              >{c.name} ({c.count})</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-72 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <p className="text-lg mb-2">No articles found</p>
            <p className="text-sm">Check back soon — we publish new trade insights regularly.</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="block mb-10 group" data-testid="featured-post">
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl overflow-hidden flex flex-col md:flex-row">
                  {featured.featured_image && (
                    <div className="md:w-1/2 h-56 md:h-auto overflow-hidden">
                      <img src={featured.featured_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className={`p-8 flex flex-col justify-center ${featured.featured_image ? 'md:w-1/2' : 'w-full'}`}>
                    <span className="text-orange-400 text-xs font-semibold uppercase tracking-wider mb-2">{featured.category}</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors">{featured.title}</h2>
                    <p className="text-blue-200 text-sm mb-4 line-clamp-3">{featured.excerpt}</p>
                    <div className="flex items-center gap-4 text-blue-300 text-xs">
                      <span className="flex items-center gap-1"><Clock size={13} />{featured.reading_time} min read</span>
                      <span className="flex items-center gap-1"><Calendar size={13} />{new Date(featured.published_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Post Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i + 1} onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${page === i + 1 ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >{i + 1}</button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t py-8 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Shanghai RunTheRays International Trade Co., Ltd.</p>
      </footer>
    </div>
  );
};

const PostCard = ({ post }) => (
  <Link to={`/blog/${post.slug}`} className="group" data-testid={`post-card-${post.id}`}>
    <article className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
      {post.featured_image && (
        <div className="h-44 overflow-hidden">
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{post.category}</span>
          <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={12} />{post.reading_time} min</span>
        </div>
        <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-900 transition-colors line-clamp-2">{post.title}</h3>
        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{post.excerpt}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{new Date(post.published_at).toLocaleDateString()}</span>
          <span className="text-orange-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight size={14} /></span>
        </div>
      </div>
    </article>
  </Link>
);

export default BlogHub;
